'use client';

import { useState, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRouter } from 'next/navigation';
import { db } from '@/lib/db';
import type { CounterSnapshot } from '@/lib/types';
import { useIsMounted, safeDate } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import { ThinSheepStrip } from '@/components/redesign/sheep-strip';
import { Card } from '@/components/ui/Card';

// 2026-05-09 build 1160 — Counter B1 zine port, second iteration.
// Veronika's CounterCard.jsx + screenshot of "THE counter" mockup
// (Telegram 2026-05-09 13:05). Per her message at 13:06 the "OF X"
// target is now an authorized new feature — single explicit
// exception to today's "visual-only" rule. Rest of the page sticks
// to visual-only changes.
// 2026-05-09 build 1163 — Veronika: "i want 'stitch' to be same font
// as 'THE KNITTERS' on the home screen". The home screen renders that
// title in `var(--font-geist-sans)` (Inter, loaded under that variable
// name in layout.tsx). The previous Archivo Narrow rendering felt
// foreign next to the home page — switching every display use on this
// page to Inter brings the whole app down to the 3-font palette
// Veronika has in mind: Inter (display), Fraunces (serif), JetBrains
// Mono (mono).
const fontDisplay = "var(--font-geist-sans), system-ui, -apple-system, sans-serif";
const fontSerif = "'Fraunces', 'Times New Roman', serif";

const HALFTONE_LIGHT = {
  backgroundImage:
    'radial-gradient(circle, rgba(15,14,12,0.25) 1px, transparent 1.4px)',
  backgroundSize: '4px 4px',
};

type CounterCardProps = {
  name: string;
  value: number;
  target: number;
  onTargetChange: (n: number) => void;
  resetLabel: string;
  /** Label before the target number — "out of" / Czech "z" (i18n). */
  ofLabel: string;
  onMinus: () => void;
  onPlus: () => void;
  onPlusTwo: () => void;
  onReset: () => void;
  // 2026-05-09 build 1167 — Veronika asked to colour the "+" button
  // per counter to visually separate A from B; colours pulled from
  // the home-screen cards palette (Yarn = #1F4FE0 blue, Tools =
  // #FCC117 yellow). `accentText` is the readable foreground for the
  // chosen accent.
  accent: string;
  accentText: string;
};

function CounterCard({ name, value, target, onTargetChange, resetLabel, ofLabel, onMinus, onPlus, onPlusTwo, onReset, accent, accentText }: CounterCardProps) {
  // 2026-05-09 build 1179 — Veronika: tapping "OF X" should drop
  // straight into editing without a click→state→focus round-trip.
  // Previous build had an `editing` toggle that swapped a button
  // for an input, but iOS Safari often consumes the user gesture
  // before the input mounts, so the keyboard never opens. Solution:
  // render the input ALWAYS so the iOS tap focuses a real, already-
  // mounted input — keyboard opens on the user's gesture itself.
  // We keep the displayed value in sync with the persisted `target`
  // prop and commit on blur or Enter.

  return (
    <Card
      style={{
        // 2026-05-09 build 1162 — Veronika asked for the two
        // counters to be separated with a gap, so each card now
        // owns its own 2px outer border again (removed the shared
        // outer-wrapper grouping from build 1160) and the parent
        // container provides the breathing room with `gap`.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Big-number "photo" block */}
      <div
        style={{
          position: 'relative',
          background: 'var(--paper-1)',
          color: 'var(--ink)',
          padding: '8px 14px 4px',
          overflow: 'hidden',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            ...HALFTONE_LIGHT,
            opacity: 0.5,
            mixBlendMode: 'multiply',
            pointerEvents: 'none',
          }}
        />

        {/* Target ("OF X") indicator — input is always mounted so
            an iOS tap immediately focuses it and opens the keyboard
            on the user's gesture. */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'baseline',
            gap: 4,
            zIndex: 1,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.16em',
            color: 'var(--ink-2)',
          }}
        >
          <label
            htmlFor={`counter-of-${name}`}
            style={{ textTransform: 'uppercase', cursor: 'pointer' }}
          >
            {ofLabel}
          </label>
          <input
            id={`counter-of-${name}`}
            type="number"
            inputMode="numeric"
            min={0}
            value={target > 0 ? String(target) : ''}
            onChange={e => {
              const n = parseInt(e.target.value, 10);
              onTargetChange(Number.isFinite(n) && n > 0 ? n : 0);
            }}
            placeholder="—"
            aria-label="Target"
            style={{
              width: 48,
              background: 'transparent',
              border: 'none',
              borderBottom: '1px dashed var(--ink-2)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              letterSpacing: '0.12em',
              color: 'var(--ink)',
              padding: '1px 2px',
              outline: 'none',
              textAlign: 'right',
              cursor: 'text',
            }}
          />
        </div>

        <div
          style={{
            position: 'relative',
            fontFamily: fontDisplay,
            fontWeight: 800,
            fontSize: 90,
            lineHeight: 0.85,
            letterSpacing: '-0.06em',
            textAlign: 'center',
            margin: '-2px 0',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </div>
      </div>

      {/* Title + reset + buttons */}
      <div
        style={{
          padding: '8px 12px',
          borderTop: '1px solid var(--ink)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              fontFamily: fontDisplay,
              fontWeight: 800,
              fontSize: 20,
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              lineHeight: 0.95,
              color: 'var(--ink)',
            }}
          >
            {name}
          </div>
          <button
            onClick={onReset}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '0.12em',
              color: 'var(--ink-3)',
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            ↺ {resetLabel}
          </button>
        </div>

        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px dashed var(--ink)',
            display: 'flex',
            gap: 6,
          }}
        >
          <button
            onClick={onMinus}
            style={{
              flex: 1,
              height: 38,
              border: '1px solid var(--ink)',
              background: 'transparent',
              color: 'var(--ink)',
              fontFamily: fontDisplay,
              fontWeight: 900,
              fontSize: 22,
              cursor: 'pointer',
            }}
          >
            −
          </button>
          <button
            onClick={onPlus}
            style={{
              flex: 2,
              height: 38,
              background: accent,
              color: accentText,
              border: 'none',
              fontFamily: fontDisplay,
              fontWeight: 900,
              fontSize: 24,
              cursor: 'pointer',
            }}
          >
            +
          </button>
          <button
            onClick={onPlusTwo}
            style={{
              flex: 1,
              height: 38,
              border: '1px solid var(--ink)',
              background: 'transparent',
              color: 'var(--ink)',
              fontFamily: fontDisplay,
              fontWeight: 900,
              fontSize: 16,
              letterSpacing: '-0.02em',
              cursor: 'pointer',
            }}
          >
            +2
          </button>
        </div>
      </div>
    </Card>
  );
}

// 2026-05-09 build 1160 — split the i18n title at the first space
// so "Stitch Counter" / "Počítadlo ok" both render as the zine
// 2-line treatment from Veronika's mockup: first word in display
// 800 uppercase, second part in italic serif lowercase. Falls back
// to a single line if the string has no space.
function splitZineTitle(raw: string): { head: string; tail: string } {
  const space = raw.indexOf(' ');
  if (space < 0) return { head: raw, tail: '' };
  return { head: raw.slice(0, space), tail: raw.slice(space + 1) };
}

export default function CounterPage() {
  const router = useRouter();
  const mounted = useIsMounted();
  const { t } = useI18n();
  const [countA, setCountA] = useState(0);
  const [countB, setCountB] = useState(0);
  const [targetA, setTargetA] = useState(0);
  const [targetB, setTargetB] = useState(0);
  const [selectedProject, setSelectedProject] = useState('');
  // 2026-05-10 build 1281 — Veronika: when restoring a saved
  // counter snapshot, the per-counter target ("of X") was being
  // dropped. Saved snapshots only carried { project, a, b, date }
  // so loading one cleared whatever targets the user had set.
  // Added optional `ta` / `tb` fields; legacy entries without
  // them just don't touch the targets on load.
  // Saved counter history now lives in the synced `counterSnapshots` table
  // (Veronika 2026-06-17) so it appears on all the user's devices. Newest
  // first; tombstoned (deletedAt) rows hidden. The LIVE counter (countA/B,
  // targets, selectedProject) deliberately stays device-local in localStorage.
  const history = useLiveQuery(async () => {
    const rows = await db.counterSnapshots.filter(s => s.deletedAt == null).toArray();
    return rows.sort((a, b) => safeDate(b.createdAt).getTime() - safeDate(a.createdAt).getTime());
  }, [], [] as CounterSnapshot[]);

  const projects = useLiveQuery(async () => {
    const all = await db.projects.toArray();
    return all.filter(p => p.deletedAt == null && (p.status === 'in-progress' || p.status === 'hibernating'));
  });

  useEffect(() => {
    setCountA(parseInt(localStorage.getItem('counter-a') || '0'));
    setCountB(parseInt(localStorage.getItem('counter-b') || '0'));
    setTargetA(parseInt(localStorage.getItem('counter-target-a') || '0'));
    setTargetB(parseInt(localStorage.getItem('counter-target-b') || '0'));
    setSelectedProject(localStorage.getItem('counter-project') || '');
  }, []);

  // One-time migration: lift any pre-existing localStorage `counter-history`
  // into the synced table so it survives and propagates. Idempotent via a
  // flag; the old key is left in place (never delete user data). Each device
  // migrates its own local history once — the union is the merged history.
  useEffect(() => {
    (async () => {
      if (localStorage.getItem('counter-history-migrated-v1')) return;
      const raw = localStorage.getItem('counter-history');
      if (raw) {
        try {
          const old = JSON.parse(raw) as Array<{ project?: string; a?: number; b?: number; ta?: number; tb?: number }>;
          // old[0] is the most recent — assign descending timestamps to keep order.
          const base = Date.now();
          for (let i = 0; i < old.length; i++) {
            const e = old[i];
            await db.counterSnapshots.add({
              project: e.project ?? 'No project',
              a: e.a ?? 0,
              b: e.b ?? 0,
              ta: e.ta,
              tb: e.tb,
              createdAt: new Date(base - i * 1000),
            } as CounterSnapshot);
          }
        } catch { /* malformed legacy history — skip, don't block */ }
      }
      localStorage.setItem('counter-history-migrated-v1', '1');
    })();
  }, []);

  useEffect(() => { localStorage.setItem('counter-a', String(countA)); }, [countA]);
  useEffect(() => { localStorage.setItem('counter-b', String(countB)); }, [countB]);
  useEffect(() => { localStorage.setItem('counter-target-a', String(targetA)); }, [targetA]);
  useEffect(() => { localStorage.setItem('counter-target-b', String(targetB)); }, [targetB]);
  useEffect(() => { localStorage.setItem('counter-project', selectedProject); }, [selectedProject]);

  const saveSnapshot = () => {
    db.counterSnapshots.add({
      project: selectedProject || 'No project',
      a: countA,
      b: countB,
      ta: targetA,
      tb: targetB,
      createdAt: new Date(),
    } as CounterSnapshot);
  };

  const loadSnapshot = (h: { project: string; a: number; b: number; ta?: number; tb?: number }) => {
    setCountA(h.a);
    setCountB(h.b);
    setTargetA(h.ta ?? 0);
    setTargetB(h.tb ?? 0);
    setSelectedProject(h.project === 'No project' ? '' : h.project);
  };

  // Tombstone, never physical delete (SYNC.md §2): set deletedAt so the
  // removal propagates to peers without risking their data.
  const deleteSnapshot = (id?: number) => {
    if (id == null) return;
    db.counterSnapshots.update(id, { deletedAt: new Date() });
  };

  // Counter-history timestamps are stored as a real Date and formatted here,
  // so devices in different locales render consistently.
  const formatSnapshotDate = (d: Date | string | undefined) =>
    safeDate(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (!mounted) return null;

  const { head: titleHead, tail: titleTail } = splitZineTitle(t.stitchCounter);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--paper-1)',
        color: 'var(--ink)',
        fontFamily: fontSerif,
        position: 'relative',
      }}
    >
      {/* B1 zine header — 2-line title treatment + short
          underline. 2026-05-09 build 1169 — Veronika: under the
          title is a "shorter line, not edge to edge", and the
          picker's bottom line goes "edge to edge". So the header
          drops its full-width borderBottom and instead carries a
          short rule INSIDE the title block (left-aligned, 60% of
          the viewport) — the full-width line lives on the picker
          row directly below. */}
      <header
        style={{
          // 2026-05-09 build 1230 — Veronika 'now create such a
          // header style everywhere'. Halftone-dot pattern under
          // the masthead so it matches the body's grain.
          position: 'sticky',
          top: 0,
          zIndex: 40,
          backgroundColor: 'var(--paper-1)',
          backgroundImage:
            'radial-gradient(circle, rgba(15,14,12,0.10) 1px, transparent 1.4px)',
          backgroundSize: '4px 4px',
          paddingTop: 'env(safe-area-inset-top)',
        }}
      >
        <div
          style={{
            padding: '10px 14px 8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <button
              onClick={() => router.back()}
              aria-label="Back"
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                fontFamily: 'var(--font-mono)',
                fontSize: 9,
                letterSpacing: '0.18em',
                color: 'var(--ink)',
                cursor: 'pointer',
                textTransform: 'uppercase',
                lineHeight: 1,
                marginBottom: 4,
                display: 'block',
              }}
            >
              ←
            </button>
            <h1
              style={{
                margin: 0,
                color: 'var(--ink)',
                // 2026-05-09 build 1180 — Veronika: "THE counter
                // will be in one line but the fonts stay". Stack
                // → single-row baseline. Display caps + italic
                // serif preserved at the same sizes (44 / 30).
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                gap: 8,
                lineHeight: 1,
              }}
            >
              <span
                style={{
                  fontFamily: fontDisplay,
                  fontWeight: 800,
                  fontSize: 44,
                  letterSpacing: '-0.02em',
                  textTransform: 'uppercase',
                  lineHeight: 0.95,
                }}
              >
                {titleHead}
              </span>
              {titleTail && (
                <span
                  style={{
                    fontFamily: fontSerif,
                    fontStyle: 'italic',
                    fontWeight: 400,
                    fontSize: 30,
                    letterSpacing: '-0.01em',
                    color: 'var(--ink)',
                    lineHeight: 1,
                  }}
                >
                  {titleTail.toLowerCase()}
                </span>
              )}
            </h1>
          </div>
        </div>
        {/* Short rule under the title — equal inset from left
            and right edges (14px each side), per Veronika
            2026-05-09 ("the line directly under 'counter' is as
            far from left edge in the right edge too"). */}
        <div
          aria-hidden
          style={{
            height: 1,
            background: 'var(--ink)',
            marginLeft: 14,
            marginRight: 14,
            marginBottom: 8,
          }}
        />
      </header>

      {/* Body */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingTop: 0,
          paddingLeft: 14,
          paddingRight: 14,
          paddingBottom:
            'calc(var(--bottom-nav-height, 64px) + env(safe-area-inset-bottom) + 50px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* Project picker — thin one-line row sandwiched between
            two 1px borders per Veronika's 2026-05-09 mockup
            ("text line, which is under 'the counter' ... visually
            it is like on home screen"). The native <select>
            handles the actual picking but is layered transparently
            over the row so the visible text + caret stays in
            charge of the look. */}
        <div
          style={{
            position: 'relative',
            // 2026-05-09 build 1169 — Veronika: the line under the
            // picker should run edge-to-edge of the screen. The
            // body's paddingLeft/Right (14px) is cancelled out
            // here with negative side margins so the borderBottom
            // visually spans the full viewport while the picker
            // content keeps its own internal padding.
            marginLeft: -14,
            marginRight: -14,
            borderBottom: '1px solid var(--ink)',
            padding: '8px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '0.16em',
              color: 'var(--ink-2)',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            {t.countingFor}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              color: 'var(--ink-3)',
              flexShrink: 0,
            }}
          >
            —
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: fontDisplay,
              fontWeight: 800,
              fontSize: 13,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selectedProject || t.noProjectSelected}
          </span>
          <span
            aria-hidden
            style={{
              fontFamily: fontDisplay,
              fontWeight: 900,
              fontSize: 14,
              lineHeight: 1,
              color: 'var(--ink)',
              flexShrink: 0,
              padding: '0 4px',
            }}
          >
            ▾
          </span>
          <select
            id="counter-project-select"
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            aria-label={t.countingFor}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
              border: 'none',
              background: 'transparent',
              fontSize: 16, // prevent iOS zoom-on-focus
            }}
          >
            <option value="">{t.noProjectSelected}</option>
            {projects?.map(p => (
              <option key={p.id} value={p.name}>
                {p.name}
                {p.status === 'hibernating' ? ` (${t.hibernating})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Counter cards — separated with breathing room per
            Veronika 2026-05-09 ("there should be a gap between
            counter A and counter B"). Each card carries its own
            2px outer border now (the shared-border grouping from
            1160 was reverted in 1162). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CounterCard
            name={t.counterA}
            value={countA}
            target={targetA}
            onTargetChange={setTargetA}
            resetLabel={t.reset}
            ofLabel={t.counterOutOf}
            onMinus={() => setCountA(Math.max(0, countA - 1))}
            onPlus={() => setCountA(countA + 1)}
            onPlusTwo={() => setCountA(countA + 2)}
            // 2026-05-09 build 1178 — Veronika: tapping RESET should
            // also clear the "OF X" target, not just the count.
            onReset={() => { setCountA(0); setTargetA(0); }}
            accent="var(--in-use)"
            accentText="#FFFFFF"
          />
          <CounterCard
            name={t.counterB}
            value={countB}
            target={targetB}
            onTargetChange={setTargetB}
            resetLabel={t.reset}
            ofLabel={t.counterOutOf}
            onMinus={() => setCountB(Math.max(0, countB - 1))}
            onPlus={() => setCountB(countB + 1)}
            onPlusTwo={() => setCountB(countB + 2)}
            onReset={() => { setCountB(0); setTargetB(0); }}
            accent="#FCC117"
            accentText="var(--ink)"
          />
        </div>

        {/* Save button */}
        <button
          onClick={saveSnapshot}
          style={{
            width: '100%',
            height: 44,
            background: 'var(--ink)',
            color: 'var(--paper-1)',
            border: '1px solid var(--ink)',
            fontFamily: fontDisplay,
            fontWeight: 800,
            // Build 1171 — Veronika: "the font of 'save snapshot' is
            // too big, also text will be only 'save'". Stepped down
            // to 14px, which lines up with the COUNTER A/B labels.
            fontSize: 14,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {t.saveSnapshot}
        </button>

        {/* Hint right under SAVE — Veronika 2026-05-09: tells the
            user "save your counts — reopen by tapping below". */}
        <p
          style={{
            fontFamily: fontSerif,
            fontStyle: 'italic',
            fontSize: 11,
            color: 'var(--ink-3)',
            margin: '-4px 0 0',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {t.countsAutoSaved}
        </p>

        {/* History */}
        {history.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                borderBottom: '1px solid var(--ink)',
                paddingBottom: 4,
                marginBottom: 8,
              }}
            >
              <h2
                style={{
                  fontFamily: fontDisplay,
                  fontWeight: 800,
                  fontSize: 13,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  margin: 0,
                  color: 'var(--ink)',
                }}
              >
                {t.history}
              </h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {history.map((h, i) => (
                <Card
                  key={h.uuid ?? h.id ?? i}
                  style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 8,
                    padding: '8px 10px',
                    color: 'var(--ink)',
                  }}
                >
                  <button
                    onClick={() => loadSnapshot(h)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 0,
                      gap: 12,
                      color: 'inherit',
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontFamily: fontDisplay,
                          fontWeight: 800,
                          fontSize: 14,
                          letterSpacing: '-0.01em',
                          textTransform: 'uppercase',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: 'var(--ink)',
                        }}
                      >
                        {h.project}
                      </div>
                      <div
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 9,
                          letterSpacing: '0.14em',
                          marginTop: 1,
                          color: 'var(--ink-2)',
                        }}
                      >
                        {formatSnapshotDate(h.createdAt)}
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        paddingRight: 4,
                        fontFamily: fontDisplay,
                        fontWeight: 800,
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: 20,
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--ink)',
                          }}
                        >
                          {h.a}
                        </div>
                        <div
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 7.5,
                            letterSpacing: '0.18em',
                            fontWeight: 700,
                            marginTop: 1,
                            color: 'var(--ink-2)',
                          }}
                        >
                          A
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: 20,
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            fontVariantNumeric: 'tabular-nums',
                            color: 'var(--ink)',
                          }}
                        >
                          {h.b}
                        </div>
                        <div
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 7.5,
                            letterSpacing: '0.18em',
                            fontWeight: 700,
                            marginTop: 1,
                            color: 'var(--ink-2)',
                          }}
                        >
                          B
                        </div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => deleteSnapshot(h.id)}
                    aria-label="Delete snapshot"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--ink-3)',
                      fontFamily: fontDisplay,
                      fontWeight: 800,
                      fontSize: 18,
                      padding: '0 4px',
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                  >
                    ×
                  </button>
                </Card>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* Thin sheep strip — matches home page (Veronika 2026-05-11
          telegram). Sits directly on top of the BottomNav, sharing
          its borderTop as the strip's bottom line — exactly the
          home-page recipe. No safe-area add: BottomNav uses
          Pattern D (paddingBottom:0) so --bottom-nav-height already
          reaches the screen edge through the iOS home-indicator zone. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 'var(--bottom-nav-height, 64px)',
          height: 26,
          borderTop: '2px solid var(--ink)',
          background: 'var(--paper-1)',
          overflow: 'hidden',
          zIndex: 30,
        }}
      >
        <ThinSheepStrip />
      </div>
    </div>
  );
}
